import useBuyerInfoFromQuery from "@modules/query-string/hooks/useBuyerInfoFromQuery";
import useSellerInfoFromQuery from "@modules/query-string/hooks/useSellerInfoFromQuery";
import useSourceInfoFromQuery from "@modules/query-string/hooks/useSourceInfoFromQuery";
import useVehicleInfoFromQuery from "@modules/query-string/hooks/useVehicleInfoFromQuery";
import {
  CustomerInformation,
  SourceInformation,
  VehicleInformation,
} from "@modules/query-string/types";
import { useEffect, useState } from "react";
import {
  BuyerSellerFieldsInput,
  FindMyVehicleInformationFormInput,
  SourceInformationFieldsInput,
  VehicleInformationFieldsInput,
} from "../types/BillOfSaleFormFields";

export interface BillOfSaleDefaultInformation {
  vehicleDetails: FindMyVehicleInformationFormInput &
    VehicleInformationFieldsInput;
  sellerInformation: BuyerSellerFieldsInput;
  buyerInformation: BuyerSellerFieldsInput;
  sourceInformation: SourceInformationFieldsInput;
}

const convertVehicleInfoToFieldsInput = (vehicle: VehicleInformation) => {
  const {
    year,
    make,
    model,
    trim,
    bodyType,
    exteriorColour,
    fuelType,
    odometer,
    listPrice,
  } = vehicle;
  return {
    year,
    make: make ?? "",
    model: model ?? "",
    trim: trim ?? "",
    bodyType: bodyType ?? "",
    exteriorColour: exteriorColour ?? "",
    fuelType: fuelType ?? "",
    odometer,
    listPrice,
  } as VehicleInformationFieldsInput;
};

const convertSourceInfoToFieldsInput = (source: SourceInformation) => {
  const {
    sourceAdId,
    sourceUserId,
    sourceSessionId,
    sourceAppId,
    sourceLocationId,
    sourceCtaId,
    email,
  } = source;
  return {
    sourceAdId,
    sourceUserId,
    sourceSessionId,
    sourceAppId,
    sourceLocationId,
    sourceCtaId,
    email,
  } as SourceInformationFieldsInput;
};

// make sure we are only getting the data that we need
const convertCustomerInfoToFieldsInput = (customer: CustomerInformation) => {
  const {
    firstName,
    lastName,
    streetAddress,
    city,
    postalCode,
    province,
    phoneNumber,
    email,
  } = customer;
  return {
    firstName,
    lastName,
    streetAddress,
    city,
    postalCode,
    province,
    phoneNumber,
    email,
  } as BuyerSellerFieldsInput;
};

const useBillOfSaleDefaultInformation = () => {
  const [bosDefaultInfo, setBosDefaultInfo] =
    useState<BillOfSaleDefaultInformation>();
  const vehicleInfoFromQuery = useVehicleInfoFromQuery();
  const buyerInfoFromQuery = useBuyerInfoFromQuery();
  const sellerInfoFromQuery = useSellerInfoFromQuery();
  const sourceInfoFromQuery = useSourceInfoFromQuery();

  useEffect(() => {
    const findMyVehicleInformation = {
      vin: vehicleInfoFromQuery.vin,
    } as FindMyVehicleInformationFormInput;
    const vehicleDetails = {
      ...findMyVehicleInformation,
      ...convertVehicleInfoToFieldsInput(vehicleInfoFromQuery),
    };
    const buyerInformation =
      convertCustomerInfoToFieldsInput(buyerInfoFromQuery);
    const sellerInformation =
      convertCustomerInfoToFieldsInput(sellerInfoFromQuery);
    const sourceInformation =
      convertSourceInfoToFieldsInput(sourceInfoFromQuery);

    setBosDefaultInfo({
      vehicleDetails,
      sellerInformation,
      buyerInformation,
      sourceInformation,
    });
  }, [
    buyerInfoFromQuery,
    sellerInfoFromQuery,
    sourceInfoFromQuery,
    vehicleInfoFromQuery,
  ]);

  return [bosDefaultInfo, setBosDefaultInfo] as const;
};

export default useBillOfSaleDefaultInformation;
